import { isValidEmail } from "@sellar-io/common";
import {
  Button,
  InputContainer,
  InputEmail,
  Switch,
} from "@sellar-io/components";
import { useState } from "react";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import type { CheckoutInvoiceMethod } from "./types";

export type InvoiceMethodOption = {
  label: string;
  type: CheckoutInvoiceMethod;
};

export type InvoiceSettingsProps = {
  id: string;
  invoiceMethodOptions: InvoiceMethodOption[];
  invoiceMethod: CheckoutInvoiceMethod;
  invoiceEmails: string[];
  hasEmailsChanged: boolean;
  orderTotal: number;
  stripeAccountId?: string;
  paymentLinkRequested?: boolean;
  onChangeInvoiceEmails: (emails: string[]) => void;
  onSubmitInvoiceEmails: () => void;
  onChangeInvoiceMethod: (invoiceMethod: CheckoutInvoiceMethod) => void;
  onChangePaymentLinkRequested?: (checked: boolean) => void;
};

export const InvoiceSettings = ({
  id,
  invoiceMethod,
  invoiceEmails,
  invoiceMethodOptions,
  hasEmailsChanged,
  stripeAccountId,
  paymentLinkRequested,
  orderTotal,
  onChangeInvoiceMethod,
  onSubmitInvoiceEmails,
  onChangeInvoiceEmails,
  onChangePaymentLinkRequested,
}: InvoiceSettingsProps) => {
  const { isActive } = useRemoteFeatureFlags();
  const isFFActive = isActive("checkoutPaymentLinkRequest");
  const showPaymentLinkToggle =
    Boolean(stripeAccountId) &&
    paymentLinkRequested !== undefined &&
    orderTotal > 0;
  const buttonText = hasEmailsChanged ? "Confirm" : "Edit";
  const [error, setErrorMessage] = useState("");

  return (
    <div className="flex flex-col gap-4">
      {isFFActive ? (
        showPaymentLinkToggle ? (
          <div className="flex items-center gap-4 rounded-lg bg-zinc-100 p-4">
            <Switch
              checked={paymentLinkRequested}
              onChange={onChangePaymentLinkRequested}
            />
            <div className="flex flex-1 flex-col">
              <span className="text-base leading-6 font-semibold text-zinc-800">
                Send a card payment link
              </span>
              <span className="text-base leading-6 font-normal text-zinc-600">
                {paymentLinkRequested
                  ? "Include a link to pay by card when payment for this order is due. This link will be sent to the email address(es) below. You can always request a card payment link later."
                  : "Switch on to send a link to pay by card to the email address(es) below when payment for this order is due. You can always request one later."}
              </span>
            </div>
          </div>
        ) : null
      ) : (
        <div className="flex flex-col">
          <span className="font-semibold">Invoice method:</span>
          <div className="flex flex-col divide-y-2 divide-zinc-300 rounded-lg border border-zinc-300">
            {invoiceMethodOptions.map((invoiceMethodOption) => (
              <label
                key={invoiceMethodOption.type}
                className="flex flex-1 cursor-pointer items-center gap-2 p-4 has-checked:bg-gray-200"
              >
                <input
                  checked={invoiceMethodOption.type === invoiceMethod}
                  name={`${invoiceMethodOption.type}-${id}`}
                  value={invoiceMethodOption.type}
                  type="radio"
                  className="h-4 w-4 appearance-none rounded-full border-2 border-gray-800 text-black checked:border-[6px] checked:bg-white"
                  onChange={(event) =>
                    onChangeInvoiceMethod(
                      event.target.value as CheckoutInvoiceMethod,
                    )
                  }
                />
                <span className="font-semibold">
                  {invoiceMethodOption.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <span className="font-semibold">Invoice email addresses</span>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!invoiceEmails.every((email) => isValidEmail(email.trim()))) {
              setErrorMessage("Only provide valid emails, separated by commas");
              return;
            }
            onSubmitInvoiceEmails();
          }}
          className="flex gap-2"
        >
          <InputContainer className="flex flex-1">
            <InputEmail
              multiple
              value={invoiceEmails}
              onChange={(event) => {
                onChangeInvoiceEmails(event.target.value.split(","));
                setErrorMessage("");
              }}
              type="text"
              required
              placeholder="sales@example.com,accounts@example.com,invoice@example.com"
            />
          </InputContainer>
          <Button type="submit">{buttonText}</Button>
        </form>
        <span className="pl-1 text-sm text-zinc-500">
          Please note: Updating your invoice email address(es) here will update
          them across your business.
        </span>
        {error && <span className="text-sm text-red-800">{error}</span>}
      </div>
    </div>
  );
};
